import { faCalendar, faMoneyBill } from "@awesome.me/kit-3777c2145e/icons/classic/regular";
import { faCheck, faCircleQuestion, faPlay as fasPlay } from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import autobind from "autobind-decorator";
import dayjs from "dayjs";
import type { FormApi } from "final-form";
import type { ReactNode } from "react";
import { Card } from "react-bootstrap";
import { AbstractSubmitStep } from "@/component/assets/AbstractLeadForm/Steps/AbstractSubmitStep";
import { FormCurrency, FormNumber, InfoPopup, ProgressButton } from "@/shared/ui";
import { formatText } from "@/shared/ui/format";
import { DEFAULT_MONTHLY_DEPOSIT } from "../data";

export interface StepFields {
	birthYear: number;
	monthlyDeposit: number;
}

const MIN_DEPOSIT = 25_000;
const MAX_DEPOSIT = 1_000_000;

export class Step1 extends AbstractSubmitStep<StepFields> {
	protected initValues(): StepFields {
		return {
			birthYear: undefined,
			monthlyDeposit: DEFAULT_MONTHLY_DEPOSIT,
		};
	}

	protected renderContent(_values: StepFields, form: FormApi<StepFields>): ReactNode {
		const { submitState } = this.props;
		const disabled = this.isDisabled();

		return (
			<>
				<Card.Header className="bg-transparent">
					<Card.Title className="text-center">Mennyi pénzed lesz mire nyugdíjba mész?</Card.Title>
				</Card.Header>
				<Card.Body>
					<FormNumber
						label="Melyik évben születtél?"
						placeholder="Add meg a születési éved"
						name="birthYear"
						autoComplete="bday-year"
						maxLength={4}
						prepend={<FontAwesomeIcon icon={faCalendar} fixedWidth />}
						validate={this.validBirthYear}
						disabled={disabled}
						required
					/>
					<FormCurrency
						label="Mennyi pénzt tudsz félretenni havonta nyugdíjcélra?"
						placeholder="Havi megtakarítás"
						infoTooltip="Havonta ennyit fizetsz be megtakarításként – ez alapján kapod az állami támogatást is."
						name="monthlyDeposit"
						prepend={<FontAwesomeIcon icon={faMoneyBill} fixedWidth />}
						validate={this.validDeposit}
						disabled={disabled}
						required
					/>
				</Card.Body>
				<Card.Footer className="text-center">
					<ProgressButton
						type="submit"
						icon={<FontAwesomeIcon icon={fasPlay} />}
						state={submitState}
						className={`${!form.getState().valid ? "disabled" : ""} text-uppercase font-weight-bold`}
					>
						Tovább
					</ProgressButton>
					<InfoPopup
						as="div"
						className="mt-3"
						tooltip="Ha van olyan SZJA befizetésed, amiből ezt a támogatást érvényesíteni tudod"
					>
						<FontAwesomeIcon className="text-primary mr-1" icon={faCheck} size="lg" />
						<strong>20%-os</strong> (max. 280 ezer Ft-os) <strong>adójóváírás</strong>{" "}
						<FontAwesomeIcon className="text-body-tertiary" icon={faCircleQuestion} />
					</InfoPopup>
				</Card.Footer>
			</>
		);
	}
	@autobind
	private validBirthYear(year: number): string {
		if (!year) {
			return "Kérjük adjon meg születési évet!";
		}

		if (year < 1963) {
			return "A születési éve nem lehet kisebb mint 1963";
		}

		const minYear = Number(dayjs().subtract(63, "year").year());

		if (year < minYear) {
			return `A születési évnek nagyobbnak kell lennie mint ${minYear - 1}`;
		}

		const actualYear = dayjs().year();

		if (year > actualYear) {
			return `A születési éve nem lehet nagyobb mint ${actualYear}`;
		}
	}

	@autobind
	private validDeposit(deposit: number): string {
		if (Number.isNaN(deposit)) {
			return "Kérjük adja meg a havi megtakarítás összegét!";
		}

		if (deposit < MIN_DEPOSIT) {
			return `A havi megtakarítás összege nem lehet kevesebb mint ${formatText(MIN_DEPOSIT, "ft")}!`;
		}

		if (deposit > MAX_DEPOSIT) {
			return `A havi megtakarítás összege nem lehet nagyobb mint ${formatText(MAX_DEPOSIT, "ft")}!`;
		}
	}

	protected getComponentName(): string {
		return "Step1";
	}
}
